import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { FeedbackDashboard } from "@/components/feedback/FeedbackDashboard";
import { CaseDrawer } from "@/components/kanban/CaseDrawer";
import { useBrand } from "@/lib/BrandContext";
import { supabase } from "@/lib/supabaseClient";
import { getParisDateKey } from "@/lib/parisDateTime";
import type { FeedbackRow } from "@/lib/feedback";
import type { Case } from "@/lib/useRealtimeCases";
import { feedbackStaffRequest } from "@/lib/feedbackApi";
export default function FeedbackPage() {
  const { brand } = useBrand();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dossier, setDossier] = useState<Case | null>(null);
  const refresh = useCallback(async () => {
    try {
      const data = await feedbackStaffRequest();
      setRows(data.rows);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible de charger les avis.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (brand !== "smile") return;
    void refresh();
    const id = setInterval(() => {
      void refresh();
    }, 30000);
    return () => clearInterval(id);
  }, [refresh, brand]);
  if (brand !== "smile") return <Navigate to="/kanban" replace />;
  return (
    <>
      {error && (
        <div role="alert" className="m-6 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error}{" "}
          <button onClick={refresh} className="underline">
            Réessayer
          </button>
        </div>
      )}
      {loading ? (
        <p role="status" className="p-10 text-center text-gray-500">
          Chargement des avis…
        </p>
      ) : (
        <FeedbackDashboard
          rows={rows}
          initialMonth={getParisDateKey(new Date()).slice(0, 7)}
          onFollowup={async (id, status, note) => {
            await feedbackStaffRequest({ action: "followup", id, status, note });
            setRows((all) =>
              all.map((r) =>
                r.id === id ? { ...r, followup_status: status, staff_note: note } : r,
              ),
            );
          }}
          onOpenCase={async (id) => {
            const { data, error } = await supabase.from("cases").select("*").eq("id", id).single();
            if (error) setError("Impossible d’ouvrir le dossier.");
            else setDossier(data);
          }}
        />
      )}
      {dossier && <CaseDrawer case={dossier} onClose={() => setDossier(null)} />}
    </>
  );
}
